/**
 * §A.1 · La cronología de la cuenta de un club.
 *
 * ## Una sola columna de tiempo, y eso es todo el valor
 *
 * El contrato lo dice sin rodeos: «repartido en seis pantallas nadie lo cruza; junto
 * se ve que la remesa lleva tres meses fallando y que por eso llaman». Alta,
 * arranque, suscripción, remesas, incidencias y entradas en soporte, mezcladas por
 * fecha y no separadas por tipo.
 *
 * Por eso aquí **no hay filtro por clase**. Filtrar deshace lo único que hace útil
 * esta pantalla: si se puede esconder las incidencias, se esconden, y volvemos a
 * tener seis pantallas en una.
 *
 * ## Se pide a mano
 *
 * Es la única de la ficha que no carga sola, y no por ahorro: la ficha de club ya
 * lanza cuatro peticiones, y la cronología es la que se abre **cuando vas a llamar**,
 * no cada vez que miras un club por encima. Además cada lectura deja una entrada en
 * `audit_log` —`admin.academy.timeline`—, y un registro de accesos lleno de lecturas
 * que nadie pidió no explica nada.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi } from '@/shared/api/errores';
import { Cargando, Error as PanelError } from '@/shared/ui/Estados';
import { fechaYHora } from '@/shared/ui/formato';
import { claseDeEntrada, type EntradaDeCronologia } from '@/backoffice/tipos';

/** Cuántas se piden. 200 son unos dos años de una cuenta normal. */
const CUANTAS = 200;

export function CronologiaDelClub({ clubId }: { clubId: string }) {
  const [abierta, setAbierta] = useState(false);

  const cronologia = useQuery({
    queryKey: ['admin', 'cronologia', clubId],
    enabled: abierta,
    queryFn: async () => {
      const { data, error } = await api.v1.admin
        .academies({ id: clubId })
        .timeline.get({ query: { limit: CUANTAS } });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as EntradaDeCronologia[];
    },
  });

  if (!abierta) {
    return (
      <>
        <h2 className="seccion">Qué le ha pasado a esta cuenta</h2>
        <p className="tenue">
          Alta, arranque, suscripción, remesas, incidencias y entradas en
          soporte, en una sola columna de tiempo.{' '}
          <strong>Es lo que se mira antes de llamar.</strong>
        </p>
        <button type="button" onClick={() => setAbierta(true)}>
          Ver la cronología
        </button>
      </>
    );
  }

  return (
    <>
      <h2 className="seccion">Qué le ha pasado a esta cuenta</h2>

      {cronologia.isLoading && (
        <Cargando filas={5} etiqueta="Cargando la cronología" />
      )}

      {cronologia.isError && (
        <PanelError
          problema={aProblema(cronologia.error)}
          reintentar={() => void cronologia.refetch()}
        />
      )}

      {cronologia.data &&
        (cronologia.data.length === 0 ? (
          <p className="tenue">
            No hay nada todavía. Un club recién creado del censo federativo está
            así hasta que alguien lo reclama.
          </p>
        ) : (
          <>
            <ol className="cronologia">
              {cronologia.data.map((e, i) => {
                const c = claseDeEntrada(e.kind);
                return (
                  <li
                    /*
                      La clave lleva el índice porque **una entrada no tiene id**: el
                      DTO son cuatro campos y dos remesas del mismo minuto se
                      distinguirían solo por el orden. Y el orden es estable: lo fija
                      el servidor.
                    */
                    key={`${String(e.at)}-${e.kind}-${i}`}
                    className={c.clase || undefined}
                  >
                    <span className="cronologia-icono" aria-hidden="true">
                      {c.icono}
                    </span>
                    <div>
                      <div className="aviso-cabeza">
                        <strong>{e.title}</strong>
                        {/* La clase, en palabras: el icono no la dice a un lector de pantalla. */}
                        <span className="pastilla pastilla-left">
                          {c.etiqueta}
                        </span>
                      </div>
                      {e.detail && <p>{e.detail}</p>}
                      <p className="tenue">{fechaYHora(e.at)}</p>
                    </div>
                  </li>
                );
              })}
            </ol>

            {cronologia.data.length === CUANTAS && (
              /*
               * Sin cursor en el contrato: la respuesta es un array y el único mando
               * es `limit`. Decirlo es mejor que dejar creer que eso es todo.
               */
              <p className="tenue">
                Son las {CUANTAS} últimas. Si hace falta más atrás, se mira en
                el registro de accesos.
              </p>
            )}
          </>
        ))}
    </>
  );
}
